import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HiOutlineX, HiOutlineChevronDown, HiOutlineChevronRight } from 'react-icons/hi';
import { mainNavItems, mainNavGroups, adminNavItems, adminNavGroups, bottomNavItems } from '../../data/navigation';
import { useSelector } from 'react-redux';
import useAdmin from '../../hooks/useAdmin';
import AdminViewToggle from '../AdminViewToggle';
import LogoutConfirmationModal from '../LogoutConfirmationModal';
import './Sidebar.scss';

const Sidebar = ({ isCollapsed, mobileOpen, onCloseMobile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  
  // Use our custom hook to get admin status and view mode
  const { isAdmin, isAuthenticated, adminViewActive, toggleAdminViewMode } = useAdmin();
  const { canAssessRecruitment } = useSelector((state) => state.auth);
  const currentPath = location.pathname;
  
  // Debug logging - can be removed after fixing issues
  useEffect(() => {
    console.log('Sidebar using admin status:', { 
      isAuthenticated, 
      isAdmin, 
      adminViewActive,
      currentPath
    });
  }, [isAuthenticated, isAdmin, adminViewActive, currentPath]);
  
  // Check if this is a parent with the current path
  useEffect(() => {
    const navItemsToDisplay = (isAdmin && adminViewActive) ? adminNavItems : mainNavItems;
    
    navItemsToDisplay.forEach(item => {
      if (item.subItems && item.subItems.some(subItem => subItem.path === currentPath)) {
        setExpandedItems(prev => ({ ...prev, [item.path]: true }));
      }
    });
  }, [currentPath, isAdmin, adminViewActive]);
  
  // Handle admin view mode toggle with notification
  const handleAdminViewToggle = () => {
    toggleAdminViewMode();
    
    // Show notification
    const newMode = !adminViewActive;
    setNotification(newMode ? 'Switched to Admin View' : 'Switched to User View');
    
    // Clear notification after 3 seconds
    setTimeout(() => {
      setNotification(null);
    }, 3000);
    
    // If on an admin route and switching to user view, redirect to home
    if (!newMode && currentPath.startsWith('/admin')) {
      navigate('/');
    }
  };
  
  const handleLogout = (e) => {
    e.preventDefault();
    setIsLogoutModalOpen(true);
  };
  
  const toggleExpand = (path) => {
    setExpandedItems(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };
  
  const NavItem = ({ icon, title, path, isActive, isCollapsed, onClick, hasChildren, isExpanded }) => {
    const handleClick = (e) => {
      if (onClick) {
        onClick(e);
      }
    };

    return (
      <li className={`nav-item ${isActive ? 'active' : ''} ${hasChildren ? 'has-children' : ''} ${isExpanded ? 'expanded' : ''}`}>
        {hasChildren ? (
          <div 
            className="nav-link"
            onClick={() => toggleExpand(path)}
          >
            <span className="nav-icon">{icon}</span>
            {!isCollapsed && (
              <>
                <span className="nav-title">{title}</span>
                <span className="expand-icon">
                  {isExpanded ? <HiOutlineChevronDown /> : <HiOutlineChevronRight />}
                </span>
              </>
            )}
          </div>
        ) : (
          <Link 
            to={path} 
            className="nav-link"
            onClick={handleClick}
          >
            <span className="nav-icon">{icon}</span>
            {!isCollapsed && <span className="nav-title">{title}</span>}
          </Link>
        )}
      </li>
    );
  };
  
  const renderNavItems = () => {
    // Choose the appropriate navigation groups based on admin status
    const navGroups = (isAdmin && adminViewActive) ? adminNavGroups : mainNavGroups;
    const filteredGroups = navGroups.map((group) => ({
      ...group,
      items: (group.items || []).filter((item) => {
        if (item.requiresRecruitmentAssessor) {
          return Boolean(canAssessRecruitment);
        }
        return true;
      }),
    })).filter((group) => group.items.length > 0);
    
    return filteredGroups.map((group, groupIndex) => (
      <div key={groupIndex} className={`nav-group nav-group-${group.title.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}>
        {!isCollapsed && (
          <div className="nav-group-header">
            <span className="nav-group-title">{group.title}</span>
          </div>
        )}
        <div className="nav-group-items">
          {group.items.map((item, itemIndex) => {
            const hasChildren = item.subItems && item.subItems.length > 0;
            const isExpanded = expandedItems[item.path];
            const isActive = currentPath === item.path || 
                            (hasChildren && item.subItems.some(subItem => subItem.path === currentPath));
            
            return (
              <React.Fragment key={itemIndex}>
                <NavItem 
                  icon={item.icon} 
                  title={item.title} 
                  path={item.path}
                  isActive={isActive}
                  isCollapsed={isCollapsed}
                  hasChildren={hasChildren}
                  isExpanded={isExpanded}
                  onClick={item.onClick}
                />
                
                {hasChildren && isExpanded && !isCollapsed && (
                  <ul className="sub-nav-list">
                    {item.subItems.map((subItem, subIndex) => (
                      <li 
                        key={subIndex} 
                        className={`sub-nav-item ${currentPath === subItem.path ? 'active' : ''}`}
                      >
                        <Link to={subItem.path} className="sub-nav-link">
                          <span className="sub-nav-icon">{subItem.icon}</span>
                          <span className="sub-nav-title">{subItem.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    ));
  };
  
  const sidebarClasses = `
    sidebar 
    ${isCollapsed ? 'collapsed' : ''} 
    ${mobileOpen ? 'mobile-open' : ''}
  `;

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="sidebar-backdrop" onClick={onCloseMobile}></div>
      )}
      
      <div className={sidebarClasses}>
        <div className="sidebar-header">
          {mobileOpen && (
            <button 
              className="mobile-close-button" 
              onClick={onCloseMobile}
            >
              <HiOutlineX />
            </button>
          )}
        </div>
        
        {/* View Mode Notification */}
        {notification && (
          <div className="view-mode-notification">
            {notification}
          </div>
        )}
        
        {/* Admin View Toggle (only shown for admins) */}
        {isAdmin && (
          <AdminViewToggle 
            isActive={adminViewActive}
            onToggle={handleAdminViewToggle}
            isCollapsed={isCollapsed}
          />
        )}
        
        <div className="sidebar-content">
          <nav className="sidebar-nav">
            {isAdmin && adminViewActive && (
              <div className="nav-section-header">
                {!isCollapsed && <h3>Admin</h3>}
              </div>
            )}
            <div className="nav-container">
              {renderNavItems()}
            </div>
          </nav>
        </div>
        <div className="sidebar-footer">
          <ul className="nav-list">
            {bottomNavItems.map((item, index) => (
              <NavItem 
                key={index} 
                icon={item.icon} 
                title={item.title} 
                path={item.path}
                isActive={currentPath === item.path}
                isCollapsed={isCollapsed}
                onClick={item.isLogout ? handleLogout : undefined} 
              />
            ))}
          </ul>
        </div>
        
        {/* Logout Confirmation Modal */}
        <LogoutConfirmationModal 
          isOpen={isLogoutModalOpen}
          onClose={() => setIsLogoutModalOpen(false)}
        />
      </div>
    </>
  );
};

export default Sidebar; 